/**
 * Comissão do consultor (PDF, Etapa 17) — apuração MANUAL.
 *
 * O time comercial definiu em 04/08 que na primeira versão não há cálculo: o parceiro
 * informa e o operador lança à mão, mês a mês para operações recorrentes. Espelha
 * `dto/LancamentoComissaoResponse` — confira campo a campo ao mexer.
 */

export type StatusComissao = 'PROJETADA' | 'CONFIRMADA' | 'CANCELADA'

/**
 * Etapa do ciclo que o consultor vê no Financeiro (Figma 26:339 / 37:2):
 * Aguardando NF → NF recebida → Programado → Pago. DERIVADA no backend
 * (`EstadoFinanceiroComissao.derivar`) — a tela só rotula, nada é calculado aqui.
 */
export type EstadoFinanceiroComissao =
  | 'PROJETADA'
  | 'AGUARDANDO_NF'
  | 'NF_RECEBIDA'
  /** AN-9 (Q36): a nota da linha tem pendência ABERTA — o pagamento fica bloqueado. */
  | 'NF_COM_PENDENCIA'
  | 'PROGRAMADO'
  | 'PAGO'
  | 'CANCELADA'
  /** P5 (PP5-valr B3): linha de ESTORNO do cancelamento em competência fechada — fora da NF. */
  | 'ESTORNO'
  /** P5 (PP5-valr B3): lançamento anulado por um ESTORNO (status gravado continua CONFIRMADA) — fora da NF. */
  | 'ESTORNADO'

export interface LancamentoComissao {
  id: string
  oportunidadeId: string
  numeroReserva?: number
  razaoSocial?: string
  consultorId: string
  /** Mês de referência, sempre no dia 1. */
  competencia: string
  valorProjetado: number
  valorConfirmado?: number
  /** Confirmado quando existe, senão projetado; zero se cancelado. Vem pronto do backend. */
  valorEfetivo: number
  status: StatusComissao
  /** Se este valor já pode entrar numa nota fiscal do consultor. */
  disponivelParaNota: boolean
  recorrente?: boolean
  observacoes?: string
  motivoCancelamento?: string
  lancadoEm: string
  confirmadoEm?: string
  canceladoEm?: string
  /** Ver `EstadoFinanceiroComissao`. Ausente = backend anterior ao A15. */
  estadoFinanceiro?: EstadoFinanceiroComissao
  notaFiscalId?: string | null
  notaFiscalNumero?: string | null
  /** Data programada pelo Financeiro (V44); nula até programar. */
  dataPrevistaPagamento?: string | null
  /** Data da baixa da nota, quando paga. */
  dataPagamento?: string | null
  /** Produto da reserva; a tela resolve o nome. Nulo nas reservas antigas. */
  produtoId?: string | null
  /**
   * Receita Originei informada pelo Financeiro ao confirmar (V54, Q25). SÓ back-office: o
   * backend manda `null` para o consultor. Nunca vem da Simulação.
   */
  receitaOrigineiConfirmada?: number | null
  /** Confirmada + ajustes (correções e estorno), pronta do backend. Só back-office. */
  receitaOrigineiLiquida?: number | null
  /** Se há ajuste registrado (chip "ajustada"). Só back-office. */
  receitaAjustada?: boolean | null
  // ─── P5 (AN-PP5 §4): campos novos no FIM de `LancamentoComissaoResponse` ───
  /** LANCAMENTO (o normal) ou ESTORNO (linha negativa de cancelamento em competência fechada). Ausente = backend anterior à P5. */
  tipo?: TipoLancamentoComissao
  /** Só no ESTORNO: o lançamento estornado. */
  estornaLancamentoId?: string | null
  /** Mês a que o valor se refere quando lançado depois do fechamento (dia 1). Linha "Ref. mm/aaaa". */
  competenciaReferencia?: string | null
  /** Gravada pelo servidor a partir da política do produto (ou do marco). Nula = lançamento legado. */
  recorrencia?: RecorrenciaComissao | null
  /** Derivado no backend: a competência do lançamento já foi fechada pelo Financeiro. */
  competenciaFechada?: boolean
  /** Derivado no backend: este lançamento tem um ESTORNO. */
  estornado?: boolean
  versaoPolitica?: string | null
}

export type TipoLancamentoComissao = 'LANCAMENTO' | 'ESTORNO'

// ─── P5 — recorrência e marco de ativação (AN-PP5 §1.1, §3.5, §4) ───

/** Papel de realm complementar (AN-PP5 §1.3), mesmo padrão do `DPO`. */
export const PAPEL_FINANCEIRO = 'FINANCEIRO'

/** Da política do produto (modelo de comissão), nunca de um checkbox. */
export type RecorrenciaComissao = 'RECORRENTE' | 'UNICA'

export type EventoAtivacao = 'OPERACAO_ATIVA' | 'CAMPANHA_OPERANDO' | 'NEGOCIO_FECHADO_OPERACIONAL'

export type TipoMarcoComissao = 'ATIVACAO' | 'ENCERRAMENTO' | 'REVOGACAO'

export type OrigemMarcoComissao = 'MANUAL' | 'AUTOMATICO' | 'LEGADO'

/**
 * Estado da recorrência da reserva, DERIVADO no backend (`GET …/recorrencia`). A tela só
 * rotula; o texto pronto vem em `RecorrenciaReserva.descricaoEstado`.
 */
export type EstadoRecorrenciaReserva =
  | 'SEM_POLITICA'
  | 'AGUARDANDO_ATIVACAO'
  | 'RECORRENTE_ATIVA'
  | 'RECORRENCIA_ENCERRADA'
  | 'UNICA_AGUARDANDO_LANCAMENTO'
  | 'UNICA_LANCADA'

/** Política de comissão do produto projetada em oportunidades (evento `politica-publicada`). */
export interface PoliticaComissaoProjetada {
  politicaId: string
  produtoId: string
  produtoCodigo?: string | null
  versao: string
  modelo: string
  /** Nula = produto sem política de comissão (CAPEX, Outra necessidade). */
  recorrencia: RecorrenciaComissao | null
  eventoAtivacao: EventoAtivacao | null
  vigenciaInicio: string
  vigenciaFim?: string | null
}

/** Marco append-only (`marcos_comissao_reserva`). */
export interface MarcoComissao {
  id: string
  tipo: TipoMarcoComissao
  eventoAtivacao?: EventoAtivacao | null
  /** Data real do marco (`YYYY-MM-DD`), não a do registro. */
  dataMarco: string
  marcoRevogadoId?: string | null
  politicaId?: string | null
  versaoPolitica?: string | null
  modelo?: string | null
  recorrencia: RecorrenciaComissao
  origem: OrigemMarcoComissao
  motivo?: string | null
  /** Omitido para o consultor (AN-PP5 §4). */
  registradoPor?: string | null
  registradoEm: string
  /** Derivado no backend: já revogado por um REVOGACAO posterior. */
  revogado?: boolean
}

export interface RecorrenciaReserva {
  oportunidadeId: string
  politica: PoliticaComissaoProjetada | null
  marcos: MarcoComissao[]
  estado: EstadoRecorrenciaReserva
  /** Texto pronto do backend: "Recorrente desde 03/2026 · Operação ativa", "Única — lançada"… */
  descricaoEstado: string
  /**
   * Frase de confirmação exigida para registrar a ATIVACAO conforme o evento da política
   * ("Operação ativa", "Campanha operando", "Negócio fechado e operacional"). Nula quando não
   * há ativação a registrar.
   */
  confirmacaoAtivacao?: string | null
  /** Rótulo do evento da política ("Operação ativa"…), pronto do backend. */
  rotuloEventoAtivacao?: string | null
}

/** `POST /v1/comissoes/reservas/{oportunidadeId}/marcos` — ATIVACAO ou ENCERRAMENTO. */
export interface RegistrarMarcoRequest {
  tipo: 'ATIVACAO' | 'ENCERRAMENTO'
  /** Só ATIVACAO: tem de ser o da política vigente (422 `EVENTO_ATIVACAO_DIVERGENTE`). */
  eventoAtivacao?: EventoAtivacao | null
  /** `YYYY-MM-DD`, ≤ hoje. */
  dataMarco: string
  /** Só ATIVACAO: a frase de `confirmacaoAtivacao`. */
  confirmacao?: string | null
  /** ENCERRAMENTO: obrigatório ("Originei deixou de receber"…). */
  motivo?: string | null
}

export interface ConfirmarComissaoRequest {
  /** Ausente mantém a projeção. */
  valorConfirmado?: number | null
  /** Obrigatória (zero aceito) — entra na meta de receita pela competência do lançamento. */
  receitaOrigineiConfirmada: number
  observacoes?: string | null
}

export type TipoAjusteReceita = 'CORRECAO' | 'ESTORNO'

/** Ajuste append-only da receita Originei (V54). Só back-office. */
export interface AjusteReceita {
  id: string
  /** Mês em que o ajuste entra na meta (dia 1). */
  competencia: string
  tipo: TipoAjusteReceita
  valor: number
  receitaAnterior: number
  receitaNova: number
  motivo: string
  criadoPor: string
  criadoEm: string
}

export interface AjusteReceitaRequest {
  /** `YYYY-MM-DD` — normalizado para o dia 1 no backend. */
  competencia: string
  /** Diferença, positiva ou negativa, diferente de zero. */
  valor: number
  motivo: string
}

export interface ProgramarComissaoRequest {
  /** `YYYY-MM-DD` */
  dataPrevista: string
}

export interface ResumoComissao {
  /** Soma das CONFIRMADAS — o "valor disponível para NF" da Etapa 17. */
  disponivelParaNota: number
  /** Soma das PROJETADAS: expectativa, ainda não devida. */
  projetado: number
  canceladoNoPeriodo: number
  quantidadeDisponivel: number
  quantidadeProjetada: number
}

/**
 * P5 (AN-PP5 §3.1): `recorrente` SAIU do envio — a recorrência vem da política do produto,
 * gravada pelo servidor (o backend ignora o campo se chegar).
 */
export interface LancarComissaoRequest {
  competencia: string
  valorProjetado: number
  /**
   * Mês fechado a que o valor se refere (`YYYY-MM-DD`, dia 1), quando lançado em competência
   * aberta posterior (409 `COMPETENCIA_FECHADA` sugere). Ausente = o próprio mês da competência.
   */
  competenciaReferencia?: string | null
  observacoes?: string | null
}

// ─── Nota fiscal da comissão e liquidação ───
//
// "O consultor deverá emitir a NF para que a Originei possa efetuar o pagamento da comissão
// para ele, não há um mínimo" (time comercial, 04/08). A nota é emitida NO SISTEMA DO
// CONSULTOR — a plataforma registra e acompanha o pagamento, não emite documento fiscal.

/**
 * AN-9 (Q36): ENVIADA → DADOS_CONFIRMADOS (conferência do Financeiro) → APROVADA_FISCAL →
 * PAGA; RECUSADA a partir de qualquer um dos três primeiros (inclui "retirada pelo consultor").
 */
export type StatusNotaFiscal =
  | 'ENVIADA'
  | 'DADOS_CONFIRMADOS'
  | 'APROVADA_FISCAL'
  | 'PAGA'
  | 'RECUSADA'

export interface NotaFiscalComissao {
  id: string
  consultorId: string
  numero: string
  dataEmissao: string
  valor: number
  documentoId?: string
  status: StatusNotaFiscal
  observacoes?: string
  motivoRecusa?: string
  dataPagamento?: string
  comprovanteId?: string
  /** Meses das comissões cobertas, para reconhecer a nota sem abrir o detalhe. */
  competenciasCobertas: string[]
  quantidadeComissoes: number
  enviadaEm: string
  /** Leitura automática do arquivo (OCR + IA). Nula enquanto não há arquivo anexado. */
  extracao?: NotaFiscalExtracao | null
  // ─── AN-9 (Q36) — tudo pronto do backend; a tela só exibe ───
  /** Pendências da nota (todas; a tela filtra `status === 'ABERTA'` para os bloqueios). */
  pendencias?: NotaFiscalPendencia[] | null
  /** Bloqueio pronto do backend (há pendência ABERTA). */
  possuiPendenciaAberta?: boolean | null
  /** Última conferência humana (append-only no backend). Nula enquanto não conferida. */
  conferencia?: NotaFiscalConferencia | null
  /** Registro da aprovação fiscal. Nulo fora de APROVADA_FISCAL/PAGA (e nas PAGAS legadas). */
  fiscal?: NotaFiscalFiscal | null
  /** `true` quando o POST foi um replay idempotente (nada novo gravado). */
  replay?: boolean | null
  /** Snapshots gravados no registro: soma das comissões, diferença e tolerância aplicada. */
  somaComissoes?: number | null
  diferencaConciliacao?: number | null
  toleranciaAplicada?: number | null
  // ─── Onda 5 (AN-11 §3.2–3.4) — verificação do arquivo anexado, pronta do backend ───
  /**
   * Situação do arquivo anexado: nula sem arquivo; `PENDENTE` até o vínculo do documento
   * chegar a oportunidades; `LEGADO` em nota já decidida antes da V59. Ausente = backend
   * anterior à Onda 5 (a tela não bloqueia por ela).
   */
  documentoVerificacao?: VerificacaoArquivoNf | null
  /** Por que o arquivo foi recusado (só com `RECUSADO`) — nunca diz de quem é o arquivo. */
  documentoVerificacaoMotivo?: MotivoRecusaArquivoNf | null
  /**
   * Texto do motivo, pronto do backend (nulo fora de `RECUSADO`). A tela usa o texto LOCAL do
   * código (sempre neutro) e só recorre a este quando o código é desconhecido: para o
   * back-office o backend manda o detalhe (ex.: de que empresa é o arquivo), que não vai à tela.
   */
  documentoVerificacaoDescricao?: string | null
}

/** Onda 5: `PENDENTE | VERIFICADO | RECUSADO | LEGADO` (CHECK da V59 de oportunidades). */
export type VerificacaoArquivoNf = 'PENDENTE' | 'VERIFICADO' | 'RECUSADO' | 'LEGADO'

/**
 * Onda 5: motivo da recusa (`VerificacaoArquivoNf.avaliar`, AN-11 §3.3). O consultor só recebe
 * `ARQUIVO_INVALIDO` ou `REMOVIDO` (o backend colapsa TIPO/OUTRA_NOTA/OUTRO_TENANT/OUTRO_DONO para
 * não virar oráculo de outro tenant/dono); o back-office recebe o detalhe.
 */
export type MotivoRecusaArquivoNf =
  | 'TIPO'
  | 'OUTRA_NOTA'
  | 'OUTRO_TENANT'
  | 'OUTRO_DONO'
  | 'REMOVIDO'
  | 'ARQUIVO_INVALIDO'

// ─── Leitura automática do arquivo da nota (23/10/2026) ───
//
// O consultor registra a nota e anexa o arquivo; o texto sai do OCR de originei-documentos
// e os campos, do originei-ia. A leitura NÃO substitui o informado: divergência é alerta
// para o back-office, que segue pagando ou recusando como antes.

export type StatusExtracaoNotaFiscal = 'PENDENTE' | 'CONCLUIDA' | 'SEM_DADOS' | 'FALHOU'

export interface DivergenciaNotaFiscal {
  campo: string
  rotulo: string
  informado: string
  lido: string
}

/** AN-9 §1: de onde vieram os campos — XML (parser determinístico) ou OCR + IA. */
export type OrigemExtracaoNotaFiscal = 'XML' | 'OCR_IA'

export interface NotaFiscalExtracao {
  status: StatusExtracaoNotaFiscal
  mensagem?: string | null
  documentoId: string
  /** AN-9: nula nas leituras anteriores à V57. */
  origem?: OrigemExtracaoNotaFiscal | null
  /** Layout reconhecido no XML (ex.: NFSE_NACIONAL, ABRASF, NFE). */
  layout?: string | null
  /** Competência lida (dia 1). */
  competencia?: string | null
  /** Situação lida do XML (ex.: AUTORIZADA, CANCELADA, SUBSTITUIDA). */
  situacao?: string | null
  codigoServico?: string | null
  naturezaOperacao?: string | null
  optanteSimples?: boolean | null
  regimeEspecial?: string | null
  chaveAcesso?: string | null
  /** Situação fiscal sugerida pelo backend para pré-carregar a conferência. */
  situacaoFiscalSugerida?: SituacaoFiscalNota | null
  numero?: string | null
  serie?: string | null
  codigoVerificacao?: string | null
  dataEmissao?: string | null
  municipio?: string | null
  emitenteCnpj?: string | null
  emitenteRazaoSocial?: string | null
  tomadorCnpj?: string | null
  tomadorRazaoSocial?: string | null
  discriminacao?: string | null
  valorServicos?: number | null
  valorDeducoes?: number | null
  /** Percentual: 5 = 5%. */
  aliquotaIss?: number | null
  valorIss?: number | null
  issRetido?: boolean | null
  valorIr?: number | null
  valorPis?: number | null
  valorCofins?: number | null
  valorCsll?: number | null
  valorInss?: number | null
  valorLiquido?: number | null
  /** campo → confiança (0 a 1) da leitura. */
  confiancas?: Record<string, number> | null
  divergencias: DivergenciaNotaFiscal[]
  solicitadaEm: string
  concluidaEm?: string | null
}

export interface RegistrarNotaFiscalRequest {
  numero: string
  dataEmissao: string
  /** Valor DIGITADO pelo consultor (o da nota emitida) — nunca a soma calculada na tela. */
  valor: number
  /** Comissões cobertas. O valor precisa bater com a soma delas, dentro da tolerância. */
  lancamentoIds: string[]
  documentoId?: string | null
  observacoes?: string | null
}

// ─── AN-9 (L10 · Q36) — conciliação, conferência, aprovação fiscal, pendências, política ───
//
// Contrato de `docs/agentes/visao-consultor/AN9-nota-fiscal.md` §2–§5, alinhado aos DTOs reais
// do DEV-G (`feat/vc-dev-g-nf-q36`). Erros: `ApiResponse {success:false, message, errorCode,
// correlationId, timestamp}` — o código chega em `RequisicaoFalhou.data.errorCode`.

/** `POST /v1/comissoes/notas-fiscais/conciliacao` — pré-validação, nada é gravado. */
export interface ConciliacaoNotaRequest {
  /** Valor digitado; ausente = só a soma das comissões. */
  valor?: number | null
  lancamentoIds: string[]
}

/** Tudo calculado no backend; a tela não soma nem compara. */
export interface ConciliacaoNotaResponse {
  somaComissoes: number
  valorNota?: number | null
  diferenca?: number | null
  toleranciaAplicada: number
  dentroDaTolerancia?: boolean | null
  /** Versão da política aplicada; nula sem política vigente (tolerância 0). */
  politicaVersao?: number | null
  /** CNPJ que deve constar como tomador na nota (política vigente); nulo sem política. */
  cnpjTomador?: string | null
  quantidadeComissoes?: number | null
}

export type SituacaoFiscalNota = 'REGULAR' | 'CANCELADA' | 'SUBSTITUIDA' | 'NAO_VERIFICADA'
export type FonteSituacaoFiscal = 'XML' | 'CONFERENCIA_MANUAL'

/** `POST /v1/comissoes/notas-fiscais/{id}/conferir` — confirmação humana do Financeiro. */
export interface ConferirNotaRequest {
  numero: string
  /** `YYYY-MM-DD` */
  dataEmissao: string
  /** `YYYY-MM-01` */
  competencia: string
  valorServicos: number
  emitenteCnpj: string | null
  tomadorCnpj: string | null
  situacaoFiscal: SituacaoFiscalNota
  situacaoFonte: FonteSituacaoFiscal
  situacaoEvidenciaDocumentoId?: string | null
  /** Grava o CNPJ do prestador como do consultor (1ª conferência). */
  confirmarCnpjPrestador?: boolean
}

export interface NotaFiscalConferencia {
  id?: string
  versao: number
  documentoId: string
  origemExtracao?: 'XML' | 'OCR_IA' | 'MANUAL' | null
  numero: string
  dataEmissao: string
  competencia: string
  valorServicos: number
  emitenteCnpj?: string | null
  tomadorCnpj?: string | null
  situacaoFiscal: SituacaoFiscalNota
  situacaoFonte: FonteSituacaoFiscal
  situacaoEvidenciaDocumentoId?: string | null
  somaComissoes: number
  toleranciaAplicada: number
  /** campo → { lido, confirmado } — só exibido. */
  camposCorrigidos?: Record<string, { lido?: unknown; confirmado?: unknown }> | null
  /** Se a conferência aponta o arquivo atual da nota (arquivo novo a invalida). */
  documentoAtual?: boolean | null
  /** Nulo para o consultor. A tela não exibe (nunca o nome do operador). */
  conferidoPor?: string | null
  conferidoEm: string
}

export type RegimeTributario =
  | 'SIMPLES_NACIONAL'
  | 'MEI'
  | 'LUCRO_PRESUMIDO'
  | 'LUCRO_REAL'
  | 'OUTRO'

/**
 * `POST /v1/comissoes/notas-fiscais/{id}/aprovar-fiscal` — SÓ REGISTRO (Q-AN9-5): o backend
 * confere `valorServicos − Σ retenções = líquido` (±0,01); a tela não calcula imposto.
 */
export interface AprovarFiscalRequest {
  regimeTributario: RegimeTributario
  naturezaOperacao: string | null
  codigoServico: string | null
  issRetido: boolean
  valorIssRetido: number
  valorIrRetido: number
  valorInssRetido: number
  valorPisRetido: number
  valorCofinsRetido: number
  valorCsllRetido: number
  valorLiquidoPagamento: number
  observacao?: string | null
  // ─── P5 (AN-PP5 §6.3) — no fim de `AprovarFiscalRequest` ───
  /** Município de incidência do ISS (IBGE, 7 dígitos); sem ele o ISS fica sem sugestão. */
  municipioIncidenciaIss?: string | null
  /**
   * Obrigatória quando algum valor confirmado diverge da sugestão da política em mais de R$ 0,01 —
   * quem decide é o backend (422 `RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA`, `data.divergencias`).
   */
  justificativaDivergencia?: string | null
}

export interface NotaFiscalFiscal {
  regimeTributario?: RegimeTributario | null
  naturezaOperacao?: string | null
  codigoServico?: string | null
  issRetido?: boolean | null
  valorIssRetido?: number | null
  valorIrRetido?: number | null
  valorInssRetido?: number | null
  valorPisRetido?: number | null
  valorCofinsRetido?: number | null
  valorCsllRetido?: number | null
  valorLiquidoPagamento?: number | null
  observacao?: string | null
  /** Nulo para o consultor. A tela não exibe. */
  aprovadoPor?: string | null
  aprovadoEm?: string | null
}

export type TipoPendenciaNf =
  | 'VALOR'
  | 'CNPJ_TOMADOR'
  | 'CNPJ_PRESTADOR'
  | 'COMPETENCIA'
  | 'SITUACAO_FISCAL'
  | 'NUMERO'
  | 'POLITICA_INCOMPLETA'

export type ResponsavelPendenciaNf = 'CONSULTOR' | 'FINANCEIRO'
export type StatusPendenciaNf = 'ABERTA' | 'RESOLVIDA' | 'CANCELADA'

/** Pendência de uma nota (`notas_fiscais_comissao_pendencias`). Só a conferência humana cria. */
export interface NotaFiscalPendencia {
  id: string
  tipo: TipoPendenciaNf
  /** Rótulo pronto do backend; a tela cai no seu mapa só se vier vazio. */
  rotulo?: string | null
  responsavel: ResponsavelPendenciaNf
  esperado?: string | null
  encontrado?: string | null
  mensagem: string
  status: StatusPendenciaNf
  resolucao?: string | null
  conferenciaId?: string | null
  criadaEm: string
  atualizadaEm?: string | null
  resolvidaEm?: string | null
}

/** Versão da política de NF (`politicas_nf_comissao`) — append-only, uma vigente por escopo. */
/**
 * P6 (AN-PP6 §3, oportunidades V64) — publicação com dupla aprovação das políticas do Financeiro (NF e retenção).
 * PENDENTE_APROVACAO: proposta que ainda não vale; ATIVA (encerrada = ATIVA com `vigenciaFim`); REJEITADA.
 */
export type StatusPublicacaoFinanceiro = 'PENDENTE_APROVACAO' | 'ATIVA' | 'REJEITADA'

/** Campos de publicação comuns a `PoliticaNfResponse` e `PoliticaRetencaoNfResponse` (backend V64). */
export interface PublicacaoPoliticaFinanceiro {
  statusPublicacao?: StatusPublicacaoFinanceiro | null
  /** Área dona: FINANCEIRO | JURIDICO (retenção); FINANCEIRO (NF). */
  owner?: string | null
  /** `sub` de quem propôs; a tela compara com `usuario.id` para desabilitar Aprovar/Rejeitar. */
  propostoPor?: string | null
  /** Sempre ≠ `propostoPor`; nulo em pendente, rejeitada e LEGADO. */
  aprovadoPor?: string | null
  aprovadoEm?: string | null
  rejeitadoPor?: string | null
  rejeitadoEm?: string | null
  motivoRejeicao?: string | null
  /** ATIVA anterior à dupla aprovação. */
  legado?: boolean | null
  /** Concorrência otimista: volta no corpo de aprovar/rejeitar (409 `CONFLITO_VERSAO`). */
  versaoRegistro?: number | null
}

export interface PoliticaNf extends PublicacaoPoliticaFinanceiro {
  id: string
  /** Nulo = global; preenchido = override do tenant. */
  tenantId?: string | null
  escopo?: 'GLOBAL' | 'TENANT' | null
  versao: number
  /** Q76 (AN-PP5 §7): só a GLOBAL tem tolerância; a linha do tenant vem nula (só CNPJ). */
  toleranciaValor: number | null
  cnpjTomador?: string | null
  vigenciaInicio: string
  vigenciaFim?: string | null
  motivo: string
  /** A tela não exibe. */
  criadoPor?: string | null
  criadoEm: string
}

/**
 * `GET /v1/comissoes/notas-fiscais/politica`. O consultor recebe só `toleranciaValor` e
 * `cnpjTomador`; o back-office recebe também `vigente` e `historico`. Sem política vigente:
 * tolerância 0 e `cnpjTomador` nulo.
 */
export interface PoliticaNfResponse {
  toleranciaValor: number
  cnpjTomador?: string | null
  vigente?: PoliticaNf | null
  /** Inclui pendente e rejeitadas (V64). */
  historico?: PoliticaNf[] | null
  /**
   * V64 (AN-PP6 §3): proposta do escopo aguardando OUTRA pessoa (FINANCEIRO/ADMIN). A tolerância e o CNPJ
   * efetivos acima NÃO a consideram.
   */
  pendente?: PoliticaNf | null
}

/**
 * `PUT /v1/comissoes/notas-fiscais/politica` — FINANCEIRO/GERENTE/ADMIN; V64: PROPÕE (202, painel com `pendente`),
 * sem mudar a vigente; vale só depois que OUTRA pessoa FINANCEIRO/ADMIN aprovar. 409 `PROPOSTA_JA_PENDENTE`.
 * Q76 (AN-PP5 §7): global exige `toleranciaValor` (422 `TOLERANCIA_OBRIGATORIA`); com `tenantId` a
 * tolerância é PROIBIDA (422 `TOLERANCIA_POR_TENANT_PROIBIDA`) e o CNPJ obrigatório
 * (422 `CNPJ_TOMADOR_OBRIGATORIO`).
 */
export interface PoliticaNfRequest {
  /** Ausente = política global; preenchido = override do tenant (só CNPJ). */
  tenantId?: string | null
  toleranciaValor: number | null
  cnpjTomador?: string | null
  motivo: string
}

/** Códigos de erro (`errorCode` do corpo) do contrato AN-9 — a tela traduz em texto legível. */
export type CodigoErroNotaFiscal =
  // 409
  | 'NF_DUPLICADA'
  | 'IDEMPOTENCIA_CONFLITO'
  | 'NF_JA_PAGA'
  | 'NF_ESTADO_INVALIDO'
  | 'NF_ESTADO_NAO_PERMITE_ANEXO'
  | 'NF_COM_PENDENCIA'
  | 'NF_NAO_APROVADA'
  | 'NF_SEM_ARQUIVO'
  | 'NF_LEITURA_PENDENTE'
  | 'NF_CONFERENCIA_DESATUALIZADA'
  | 'NF_RETIRADA_NAO_PERMITIDA'
  // 409 — Onda 5 (AN-11 §3.3): conferir/aprovar fiscal exigem arquivo VERIFICADO ou LEGADO
  | 'NF_ARQUIVO_NAO_VERIFICADO'
  | 'NF_ARQUIVO_RECUSADO'
  // 422
  | 'NF_VALOR_FORA_TOLERANCIA'
  | 'NF_RETENCOES_INCOERENTES'
  // 422 — Onda 5: arquivo de outra nota/dono/tenant, de reserva ou de outro tipo
  | 'NF_DOCUMENTO_INVALIDO'
  // 400 — Onda 5: o registro não aceita documentoId (o arquivo só referencia a nota depois)
  | 'NF_DOCUMENTO_NO_REGISTRO'
  // 403
  | 'COMISSAO_SO_BACKOFFICE'
  // P5 (AN-PP5 §6.3, §6.4, §7)
  | 'RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA'
  | 'POLITICA_RETENCAO_INCOMPLETA'
  | 'REGIME_TRIBUTARIO_OBRIGATORIO'
  | 'MUNICIPIO_INVALIDO'
  | 'TOLERANCIA_POR_TENANT_PROIBIDA'
  | 'TOLERANCIA_OBRIGATORIA'
  | 'CNPJ_TOMADOR_OBRIGATORIO'
